import { cancelPayment } from 'actions';
import { SpinnerOverlay } from 'components/SpinnerOverlay/SpinnerOverlay';
import { useEffect } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { EmbeddedRepository } from 'repositories/embedded/embedded';
import { getPaymentId, getPaymentStatus, getRecipientId } from 'selectors';
import { logger } from 'services/logger/logger';
import { RedirectUrl } from 'utils';
import { sessionStorage } from 'utils/storage';
import { windowUtils } from 'utils/windowUtils/windowUtils';

const CANCELLED_BY_SHOPPER = 'cancelled_by_shopper';

type PropsFromRedux = ConnectedProps<typeof connector>;
const SuccessRedirectComponent = ({
  recipientId,
  paymentId,
  paymentStatus,
  cancelPayment,
}: PropsFromRedux) => {
  const executeOnFinish = (redirectUrl: string) => {
    const onFinish = EmbeddedRepository.getProp('fwFinish');
    if (onFinish) {
      onFinish(redirectUrl);
      return;
    }
    windowUtils.redirectTo(redirectUrl);
  };

  useEffect(() => {
    const cancellationReason = RedirectUrl.getCancellationReason();
    if (cancellationReason === CANCELLED_BY_SHOPPER) {
      const sessionLinks = sessionStorage.getItem('paymentLinks');
      let cancellationLink = null;
      try {
        cancellationLink = JSON.parse(sessionLinks)?.cancellation?.href;
      } catch (_e) {
        // do nothing
      }
      if (cancellationLink) {
        cancelPayment(cancellationLink).then(() => {
          const newPaymentStatus = 'cancelled';
          const redirectUrl = RedirectUrl.buildWithStatus(newPaymentStatus);
          if (!redirectUrl) {
            return;
          }
          logger.log({
            description: 'cancel_payment_success',
            parameters: {
              paymentId,
            },
          });
          sessionStorage.removeItem('paymentLinks');
          executeOnFinish(redirectUrl);
        });
      }
    } else {
      const redirectUrl = RedirectUrl.get();

      if (!redirectUrl) {
        return;
      }

      logger.log({
        description: 'checkout_redirect',
        parameters: {
          recipientId,
          paymentId,
          paymentStatus,
          url: redirectUrl,
        },
      });
      executeOnFinish(redirectUrl);
    }
  });

  return <SpinnerOverlay />;
};

const mapStateToProps = (state: RootState) => ({
  paymentId: getPaymentId(state),
  paymentStatus: getPaymentStatus(state),
  recipientId: getRecipientId(state),
});

const mapDispatchToProps = {
  cancelPayment,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const SuccessRedirect = connector(SuccessRedirectComponent);
export { SuccessRedirect };
